import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const hasFetched = useRef(false);
  const isFetching = useRef(false);

  const N8N_BASE_URL = 'https://n8n.acespireconsulting.com/webhook';

  // ✅ GET linkedinAccountId FROM localStorage
  const getLinkedInAccountId = () => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    if (!accountId) {
      console.log('⚠️ No LinkedIn account selected');
      return null;
    }
    return parseInt(accountId);
  };

  // ✅ AUTO-FETCH ON PROVIDER MOUNT
  useEffect(() => {
    console.log('📍 DashboardProvider mounted');
    
    if (window.location.pathname === '/dashboard' && !hasFetched.current) {
      console.log('✅ Dashboard page detected - fetching data');
      fetchDashboardData(true);
    }
  }, []);

  // ✅ LISTEN FOR ACCOUNT CHANGES
  useEffect(() => {
    const handleAccountChange = () => {
      console.log('🔄 Account changed - refreshing dashboard');
      refreshDashboard();
    };

    window.addEventListener('accountChanged', handleAccountChange);
    return () => window.removeEventListener('accountChanged', handleAccountChange);
  }, []);

 const fetchDashboardData = async (force = false) => {
  if (hasFetched.current && !force && dashboardData) {
    console.log('⏭️ Dashboard data already cached, skipping fetch');
    return;
  }

  if (isFetching.current) {
    console.log('⚠️ Dashboard fetch already in progress, skipping...');
    return;
  }

  // ✅ CHECK IF ACCOUNT IS SELECTED
  const linkedinAccountId = getLinkedInAccountId();
  
  if (!linkedinAccountId) {
    // ✅ NO ACCOUNT SELECTED - SHOW GUEST MODE
    console.log('👤 No LinkedIn account selected - showing guest mode');
    setDashboardData({
      success: true,
      isGuestMode: true,
      latestPost: null,
      analytics: {
        currentFollowers: { count: 0, growth: '+0 in last 30 days' },
        summary: { period: 'In last 7 days', pageVisits: 0, totalLikes: 0 },
        topPosts: { period: 'In last 1 month', posts: [] },
        trackPerformance: { engagementRate: '0%', targetVsActual: '0%' }
      },
      upcomingPosts: [],
      connectedApps: [],
      moreApps: []
    });
    hasFetched.current = true;
    setLoading(false);
    return;
  }

  isFetching.current = true;
  setLoading(true);
  setError(null);
  
  try {
    console.log(`🔄 Fetching dashboard data for account ${linkedinAccountId}...`);
    const response = await fetch(`${N8N_BASE_URL}/dashboard-summary?linkedinAccountId=${linkedinAccountId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // ✅ CHECK IF RESPONSE HAS CONTENT
    const text = await response.text();
    if (!text || text.trim() === '') {
      console.log('⚠️ Empty response from server - showing guest mode');
      setDashboardData({
        success: true,
        isGuestMode: true,
        latestPost: null,
        analytics: {
          currentFollowers: { count: 0, growth: '+0 in last 30 days' },
          summary: { period: 'In last 7 days', pageVisits: 0, totalLikes: 0 },
          topPosts: { period: 'In last 1 month', posts: [] },
          trackPerformance: { engagementRate: '0%', targetVsActual: '0%' }
        },
        upcomingPosts: []
      });
      hasFetched.current = true;
      return;
    }
    
    const data = JSON.parse(text);
    console.log('✅ Dashboard data received');
    
    const dashboardInfo = Array.isArray(data) ? data[0] : data;
    setDashboardData(dashboardInfo);
    hasFetched.current = true;
  } catch (error) {
    console.error('❌ Error fetching dashboard data:', error);
    setError(error.message);
    hasFetched.current = false;
  } finally {
    setLoading(false);
    isFetching.current = false;
  }
};

  const refreshDashboard = () => {
    console.log('🔄 Manual refresh triggered');
    hasFetched.current = false;
    setDashboardData(null);
    fetchDashboardData(true);
  };

  const value = {
    dashboardData,
    loading,
    error,
    fetchDashboardData,
    refreshDashboard,
    hasFetched: hasFetched.current
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};